import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, RefreshCw, Plus, ArrowUpRight, ArrowDownRight,
  AlertTriangle, CheckCircle2, TrendingUp, Zap, Settings,
  ChevronRight, Clock, User, BarChart3, Shield, CreditCard
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { toast } from '../ui/sonner';
import IntiRechargeModal from './IntiRechargeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_TABS = [
  { id: 'wallets', label: 'Wallets', icon: Wallet },
  { id: 'ledger', label: 'Historial', icon: Clock },
  { id: 'usage', label: 'Consumo IA', icon: BarChart3 },
  { id: 'rates', label: 'Tarifas', icon: TrendingUp },
  { id: 'limits', label: 'Topes & Alertas', icon: Shield },
];

const KNOWN_CLIENTS = [
  'neuralia369',
  'NEURALIA-MLM-001',
  'NEURALIA-ORIFLAME-MVL',
  'NEURALIA-FISIOMEDIC-MVL',
];

const MODEL_COLORS = {
  claude_haiku_4_5: '#7C3AED',
  claude_sonnet_5: '#4F46E5',
  claude_opus_4_8: '#2563EB',
  gpt_5_4_mini: '#16A34A',
  elevenlabs: '#EA580C',
  whisper: '#0891B2',
  whatsapp_bsp: '#15803D',
  infra: '#6B7280',
};

const fmt = (n = 0, decimals = 2) => Number(n).toFixed(decimals);
const fmtCurrency = (n = 0) => `$${fmt(n, 4)}`;

function StatCard({ label, value, sub, icon: Icon, color = 'text-primary', testId }) {
  return (
    <div className="glass-card p-5 flex items-start gap-4" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/5 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-text-muted font-body">{label}</p>
        <p className="mt-1 text-2xl font-bold text-text-heading font-heading">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-text-muted">{sub}</p>}
      </div>
    </div>
  );
}

// ── Sección Wallets ────────────────────────────────────────────────────────────
function WalletsSection({ authFetch }) {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crediting, setCrediting] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount_inti: '', description: '' });
  const [stripeClientId, setStripeClientId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all(
      KNOWN_CLIENTS.map(cid =>
        authFetch(`${API}/inti/wallet/${cid}`)
          .then(r => r.json())
          .catch(() => null)
      )
    );
    setWallets(results.filter(Boolean));
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const doCredit = async (clientId) => {
    const amt = parseFloat(creditForm.amount_inti);
    if (!amt || amt <= 0) { toast.error('Ingresa una cantidad válida'); return; }
    if (!creditForm.description) { toast.error('Añade una descripción'); return; }
    try {
      const r = await authFetch(`${API}/inti/wallet/credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, amount_inti: amt, description: creditForm.description }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al recargar');
      toast.success(`Recargados ${amt} intis a ${clientId}`);
      setCrediting(null);
      setCreditForm({ amount_inti: '', description: '' });
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="inti-wallets-section">
      {/* Stripe modal */}
      {stripeClientId && (
        <IntiRechargeModal
          clientId={stripeClientId}
          onClose={() => setStripeClientId(null)}
          authFetch={authFetch}
        />
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-text-heading">Wallets por cliente</h3>
        <button onClick={load} disabled={loading} className="btn-secondary text-sm py-2 px-4 flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid gap-4">
        {wallets.map(w => (
          <div key={w.client_id} className="glass-card p-5" data-testid={`inti-wallet-${w.client_id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-text-heading text-sm">{w.client_id}</p>
                  <p className="text-xs text-text-muted">Actualizado {w.updated_at ? new Date(w.updated_at).toLocaleString('es') : '—'}</p>
                </div>
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Saldo</p>
                  <p className="text-xl font-bold text-primary font-heading">{fmt(w.balance_inti, 0)} <span className="text-sm font-normal">intis</span></p>
                  <p className="text-xs text-text-muted">{fmtCurrency(w.balance_usd)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Consumido</p>
                  <p className="text-xl font-bold text-orange-400 font-heading">{fmt(w.total_spent_inti, 0)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Recargado</p>
                  <p className="text-xl font-bold text-green-400 font-heading">{fmt(w.total_recharged_inti, 0)}</p>
                </div>
                <button
                  onClick={() => setCrediting(crediting === w.client_id ? null : w.client_id)}
                  className="btn-secondary text-sm py-2 px-4 flex items-center gap-1.5"
                  data-testid={`inti-credit-btn-${w.client_id}`}
                >
                  <Plus className="w-4 h-4" /> Crédito manual
                </button>
                <button
                  onClick={() => setStripeClientId(w.client_id)}
                  className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5"
                  data-testid={`inti-stripe-btn-${w.client_id}`}
                >
                  <CreditCard className="w-4 h-4" /> Stripe
                </button>
              </div>
            </div>

            {crediting === w.client_id && (
              <div className="mt-4 pt-4 border-t border-border-subtle">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="number" min="1" placeholder="Cantidad de intis"
                    value={creditForm.amount_inti}
                    onChange={e => setCreditForm(p => ({ ...p, amount_inti: e.target.value }))}
                    className="input-base flex-1"
                    data-testid="inti-credit-amount"
                  />
                  <input
                    type="text" placeholder="Descripción (ej: Pago mensual)"
                    value={creditForm.description}
                    onChange={e => setCreditForm(p => ({ ...p, description: e.target.value }))}
                    className="input-base flex-1"
                    data-testid="inti-credit-desc"
                  />
                  <button onClick={() => doCredit(w.client_id)} className="btn-primary text-sm py-2 px-5" data-testid="inti-credit-submit">
                    Confirmar recarga
                  </button>
                  <button onClick={() => setCrediting(null)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
                </div>
                <p className="mt-2 text-xs text-text-muted">1 inti = US$0.01 · Margen mínimo aplicado: 28%</p>
              </div>
            )}
          </div>
        ))}

        {wallets.length === 0 && !loading && (
          <div className="glass-card p-10 text-center text-text-muted">
            <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No hay wallets activos. Activa el flag <code className="text-primary">inti_wallet</code> y recarga el primer cliente.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sección Ledger ─────────────────────────────────────────────────────────────
function LedgerSection({ authFetch }) {
  const [clientId, setClientId] = useState(KNOWN_CLIENTS[0]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/inti/wallet/${clientId}/ledger?limit=100`);
      const j = await r.json();
      setEntries(j.entries || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [authFetch, clientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="inti-ledger-section">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-heading font-semibold text-text-heading">Historial de transacciones</h3>
        <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-base text-sm py-1.5 px-3 w-auto">
          {KNOWN_CLIENTS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={load} disabled={loading} className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        {entries.length === 0 ? (
          <div className="p-10 text-center text-text-muted">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin transacciones aún para este cliente.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle">
                  {['Fecha', 'Tipo', 'Intis', 'Descripción', 'Ref.'].map(h => (
                    <th key={h} className="text-left p-4 text-[10px] uppercase tracking-wider text-text-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-b border-border-subtle/40 hover:bg-white/3 transition-colors">
                    <td className="p-4 text-text-muted text-xs whitespace-nowrap">{new Date(e.ts).toLocaleString('es')}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${e.type === 'credit' ? 'bg-green-500/15 text-green-400' : 'bg-orange-500/15 text-orange-400'}`}>
                        {e.type === 'credit' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {e.type === 'credit' ? 'Crédito' : 'Débito'}
                      </span>
                    </td>
                    <td className="p-4 font-mono font-bold">
                      <span className={e.type === 'credit' ? 'text-green-400' : 'text-orange-400'}>
                        {e.type === 'credit' ? '+' : '-'}{fmt(Math.abs(e.amount_inti), 2)}
                      </span>
                    </td>
                    <td className="p-4 text-text-body max-w-xs truncate">{e.description || '—'}</td>
                    <td className="p-4 text-text-muted text-xs font-mono truncate max-w-[100px]">{e.reference_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sección Consumo ─────────────────────────────────────────────────────────────
function UsageSection({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/inti/admin/usage?limit=500`);
      const j = await r.json();
      setData(j);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const byModel = data?.events?.reduce((acc, e) => {
    const key = e.provider_model || 'unknown';
    if (!acc[key]) acc[key] = { model: key, price_inti: 0, cost_usd: 0, count: 0 };
    acc[key].price_inti += e.price_inti || 0;
    acc[key].cost_usd += e.cost_usd || 0;
    acc[key].count += 1;
    return acc;
  }, {});
  const chartData = Object.values(byModel || {}).sort((a, b) => b.price_inti - a.price_inti);

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="inti-usage-section">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-text-heading">Consumo global de IA</h3>
        <button onClick={load} disabled={loading} className="btn-secondary text-sm py-2 px-4 flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Costo real" value={fmtCurrency(summary.total_cost_usd)} icon={ArrowDownRight} color="text-red-400" testId="inti-cost-total" />
        <StatCard label="Precio cobrado" value={fmtCurrency(summary.total_price_usd)} icon={ArrowUpRight} color="text-green-400" testId="inti-price-total" />
        <StatCard label="Margen real" value={`${fmt(summary.margin_real_pct, 1)}%`} icon={TrendingUp} color={summary.margin_ok ? 'text-primary' : 'text-red-400'} testId="inti-margin" />
        <StatCard label="Eventos" value={data?.events?.length || 0} icon={Zap} color="text-primary" testId="inti-events-count" />
      </div>

      {chartData.length > 0 && (
        <div className="glass-card p-6">
          <h4 className="text-sm font-medium text-text-heading mb-4">Intis consumidos por modelo</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="model" tick={{ fill: '#64748B', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: '#0F1C3F', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                labelStyle={{ color: '#CBD5E1' }}
                formatter={(v) => [`${fmt(v, 2)} intis`, 'Precio']}
              />
              {chartData.map((entry, idx) => (
                <Bar key={entry.model} dataKey="price_inti" fill={MODEL_COLORS[entry.model] || '#4361EE'} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-border-subtle">
          <h4 className="text-sm font-medium text-text-heading">Desglose por modelo</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle">
                {['Modelo', 'Eventos', 'Intis cobrados', 'Costo real USD', 'Margen'].map(h => (
                  <th key={h} className="text-left p-4 text-[10px] uppercase tracking-wider text-text-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => {
                const margin = row.cost_usd > 0 ? ((row.price_inti * 0.01 - row.cost_usd) / (row.price_inti * 0.01)) * 100 : null;
                return (
                  <tr key={i} className="border-b border-border-subtle/40 hover:bg-white/3 transition-colors">
                    <td className="p-4">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: MODEL_COLORS[row.model] || '#4361EE' }} />
                        <span className="font-mono text-xs text-text-heading">{row.model}</span>
                      </span>
                    </td>
                    <td className="p-4 text-text-body">{row.count}</td>
                    <td className="p-4 font-bold text-primary font-mono">{fmt(row.price_inti, 2)}</td>
                    <td className="p-4 text-red-400 font-mono">{fmtCurrency(row.cost_usd)}</td>
                    <td className="p-4">
                      {margin !== null ? (
                        <span className={`text-xs font-medium ${margin >= 28 ? 'text-green-400' : 'text-red-400'}`}>
                          {fmt(margin, 1)}%
                          {margin >= 28 ? <CheckCircle2 className="inline w-3 h-3 ml-1" /> : <AlertTriangle className="inline w-3 h-3 ml-1" />}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
              {chartData.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-text-muted text-sm">Sin eventos de consumo todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Sección Rate Card ──────────────────────────────────────────────────────────
function RateCardSection({ authFetch }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    provider_model: '', level: 'economico', unit: 'tokens_per_million',
    cost_input_per_M_usd: '', cost_output_per_M_usd: '', cost_per_unit_usd: '',
    margin: 0.35, notes: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/inti/rate-card`);
      const j = await r.json();
      setRates(j.rates || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.provider_model) { toast.error('Ingresa el nombre del modelo'); return; }
    if (form.margin < 0.28) { toast.error('El margen mínimo es 28%'); return; }
    try {
      const body = {
        ...form,
        cost_input_per_M_usd: form.cost_input_per_M_usd ? parseFloat(form.cost_input_per_M_usd) : null,
        cost_output_per_M_usd: form.cost_output_per_M_usd ? parseFloat(form.cost_output_per_M_usd) : null,
        cost_per_unit_usd: form.cost_per_unit_usd ? parseFloat(form.cost_per_unit_usd) : null,
        margin: parseFloat(form.margin),
      };
      const r = await authFetch(`${API}/inti/rate-card`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al guardar');
      toast.success('Tarifa guardada correctamente');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const LEVEL_BADGES = { economico: 'bg-green-500/15 text-green-400', equilibrado: 'bg-blue-500/15 text-blue-400', premium: 'bg-purple-500/15 text-purple-400' };

  return (
    <div className="space-y-4" data-testid="inti-rates-section">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-text-heading">Tarjeta de tarifas</h3>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="btn-secondary text-sm py-2 px-3">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm py-2 px-4 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva tarifa
          </button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card p-6 border border-primary/20 space-y-4">
          <h4 className="text-sm font-medium text-text-heading">Nueva versión de tarifa</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-muted mb-1 block">Modelo / Proveedor</label>
              <input type="text" placeholder="ej: claude_sonnet_5" value={form.provider_model} onChange={e => setForm(p => ({ ...p, provider_model: e.target.value }))} className="input-base w-full" />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Nivel</label>
              <select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))} className="input-base w-full">
                <option value="economico">Económico</option>
                <option value="equilibrado">Equilibrado</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Unidad</label>
              <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} className="input-base w-full">
                <option value="tokens_per_million">Tokens/millón</option>
                <option value="chars">Caracteres (ElevenLabs)</option>
                <option value="minutes">Minutos (Whisper)</option>
                <option value="messages">Mensajes (WhatsApp)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Margen (mín. 28%)</label>
              <input type="number" step="0.01" min="0.28" max="1" value={form.margin} onChange={e => setForm(p => ({ ...p, margin: e.target.value }))} className="input-base w-full" />
              <p className="text-xs text-text-muted mt-1">{(parseFloat(form.margin || 0) * 100).toFixed(0)}% de margen</p>
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Costo entrada USD/millón tokens</label>
              <input type="number" step="0.01" placeholder="ej: 3.0" value={form.cost_input_per_M_usd} onChange={e => setForm(p => ({ ...p, cost_input_per_M_usd: e.target.value }))} className="input-base w-full" />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Costo salida USD/millón tokens</label>
              <input type="number" step="0.01" placeholder="ej: 15.0" value={form.cost_output_per_M_usd} onChange={e => setForm(p => ({ ...p, cost_output_per_M_usd: e.target.value }))} className="input-base w-full" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
            <button onClick={save} className="btn-primary text-sm py-2 px-5">Guardar tarifa</button>
          </div>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle">
                {['Modelo', 'Nivel', 'Unidad', 'Costo entrada', 'Costo salida', 'Margen', 'v.'].map(h => (
                  <th key={h} className="text-left p-4 text-[10px] uppercase tracking-wider text-text-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={i} className="border-b border-border-subtle/40 hover:bg-white/3 transition-colors">
                  <td className="p-4 font-mono text-xs text-text-heading">{r.provider_model}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${LEVEL_BADGES[r.level] || 'bg-gray-500/15 text-gray-400'}`}>{r.level}</span>
                  </td>
                  <td className="p-4 text-text-muted text-xs">{r.unit}</td>
                  <td className="p-4 font-mono text-xs text-orange-400">{r.cost_input_per_M_usd ? `$${r.cost_input_per_M_usd}` : '—'}</td>
                  <td className="p-4 font-mono text-xs text-orange-400">{r.cost_output_per_M_usd ? `$${r.cost_output_per_M_usd}` : '—'}</td>
                  <td className="p-4">
                    <span className={`text-xs font-medium flex items-center gap-1 ${r.margin_ok ? 'text-green-400' : 'text-red-400'}`}>
                      {r.margin_pct}%
                      {r.margin_ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    </span>
                  </td>
                  <td className="p-4 text-text-muted text-xs">v{r.version || 1}</td>
                </tr>
              ))}
              {rates.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-text-muted text-sm">Sin tarifas configuradas. Las tarifas por defecto están activas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Sección Topes ──────────────────────────────────────────────────────────────
function LimitsSection({ authFetch }) {
  const [form, setForm] = useState({
    client_id: KNOWN_CLIENTS[0], agent_id: 'global',
    max_inti_per_task: '', max_inti_per_day: '', max_inti_total: ''
  });
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCurrent = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/inti/spend-limits/${form.client_id}/${form.agent_id}`);
      const j = await r.json();
      setCurrent(j);
    } catch (e) { /* noop */ }
  }, [authFetch, form.client_id, form.agent_id]);

  useEffect(() => { loadCurrent(); }, [loadCurrent]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        client_id: form.client_id, agent_id: form.agent_id,
        max_inti_per_task: form.max_inti_per_task ? parseFloat(form.max_inti_per_task) : null,
        max_inti_per_day: form.max_inti_per_day ? parseFloat(form.max_inti_per_day) : null,
        max_inti_total: form.max_inti_total ? parseFloat(form.max_inti_total) : null,
      };
      const r = await authFetch(`${API}/inti/spend-limits`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) { const j = await r.json(); throw new Error(j.detail || 'Error'); }
      toast.success('Topes guardados');
      loadCurrent();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="inti-limits-section">
      <h3 className="text-lg font-heading font-semibold text-text-heading">Topes de gasto y alertas</h3>

      <div className="glass-card p-5 border border-amber-500/20 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-300">Sistema de alertas automático</p>
            <p className="text-xs text-text-muted mt-1">El sistema envía alertas por WhatsApp y email cuando el saldo cae al <strong>50%</strong>, <strong>80%</strong> y <strong>100%</strong> del consumo inicial. No requiere configuración adicional.</p>
          </div>
        </div>
      </div>

      <div className="glass-card p-6 space-y-5">
        <h4 className="text-sm font-medium text-text-heading">Configurar tope de gasto</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-text-muted mb-1.5 block">Cliente</label>
            <select value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} className="input-base w-full">
              {KNOWN_CLIENTS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1.5 block">Agente ID (o "global" para todos)</label>
            <input type="text" value={form.agent_id} onChange={e => setForm(p => ({ ...p, agent_id: e.target.value }))} className="input-base w-full" placeholder="global" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1.5 block">Máx. intis por tarea</label>
            <input type="number" min="0" value={form.max_inti_per_task} onChange={e => setForm(p => ({ ...p, max_inti_per_task: e.target.value }))} className="input-base w-full" placeholder="Sin límite" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1.5 block">Máx. intis por día</label>
            <input type="number" min="0" value={form.max_inti_per_day} onChange={e => setForm(p => ({ ...p, max_inti_per_day: e.target.value }))} className="input-base w-full" placeholder="Sin límite" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1.5 block">Máx. intis total (hard limit)</label>
            <input type="number" min="0" value={form.max_inti_total} onChange={e => setForm(p => ({ ...p, max_inti_total: e.target.value }))} className="input-base w-full" placeholder="Sin límite" />
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2" data-testid="inti-limits-save">
          <Settings className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar topes'}
        </button>

        {current && !current.no_limits && (
          <div className="mt-2 pt-4 border-t border-border-subtle">
            <p className="text-xs text-text-muted mb-2">Topes actuales para {current.client_id} / {current.agent_id}:</p>
            <div className="flex gap-4 flex-wrap text-xs">
              <span className="text-primary">Por tarea: {current.max_inti_per_task ?? 'sin límite'}</span>
              <span className="text-primary">Por día: {current.max_inti_per_day ?? 'sin límite'}</span>
              <span className="text-primary">Total: {current.max_inti_total ?? 'sin límite'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel principal ─────────────────────────────────────────────────────────────
export default function IntiWalletPanel({ authFetch }) {
  const [section, setSection] = useState('wallets');

  return (
    <div className="space-y-6" data-testid="inti-wallet-panel">
      {/* Header */}
      <div className="glass-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-text-heading flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-primary" />
            </span>
            Intis — Créditos IA
          </h2>
          <p className="text-text-muted text-sm mt-1">Sistema de créditos de uso · 1 inti = US$0.01 · Margen mínimo 28%</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 border border-primary/20">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          <span className="text-xs text-green-400 font-medium">Sistema activo</span>
        </div>
      </div>

      {/* Section nav */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="inti-section-tabs">
        {SECTION_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSection(t.id)}
            data-testid={`inti-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              section === t.id ? 'bg-primary text-[#0A1A3F]' : 'bg-card text-text-muted border border-border-subtle hover:border-primary/40'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Sections */}
      {section === 'wallets' && <WalletsSection authFetch={authFetch} />}
      {section === 'ledger' && <LedgerSection authFetch={authFetch} />}
      {section === 'usage' && <UsageSection authFetch={authFetch} />}
      {section === 'rates' && <RateCardSection authFetch={authFetch} />}
      {section === 'limits' && <LimitsSection authFetch={authFetch} />}
    </div>
  );
}
